import { z } from "zod";
import { Study, updateStudySchema } from "@/api/studio/studies/types/study.type";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useUpdateStudy } from "@/api/studio/studies/queries";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";

type FormData = z.infer<typeof updateStudySchema>;

/* Prop Types */
interface RenameStudyProps {
  projectId: string;
  study: Study;
}

/*
 * RenameStudyModal component
 * This component is used to rename a study
 */
export const RenameStudyModal = ({ projectId, study }: RenameStudyProps) => {
  /* Queries */
  const { mutateAsync: updateStudy, isPending: isUpdateStudyPending, isError: isUpdateStudyError } = useUpdateStudy();

  /* Hooks */
  const { close } = useModalError({ error: isUpdateStudyError });

  /* Handlers */
  const handleUpdate = async (values: FormData) => {
    await updateStudy({ projectId, studyId: study.id, values: { name: values.name, route: study.route } });
    close();
  };

  /* Render */
  return (
    <Form schema={updateStudySchema} onSubmit={handleUpdate} options={{ defaultValues: { name: study.name } }}>
      {({ register, formState: { errors } }) => (
        <>
          {/* Name */}
          <div className="flex items-center justify-between pb-4">
            <p className="text-base font-semibold">Study name</p>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>
          {/* Details */}
          <TextInput label="Study title" registration={register("name")} error={errors.name?.message} className="mb-4" placeholder="Enter a name" />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isUpdateStudyPending}>
              {isUpdateStudyPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
